import { useEffect, useState, useCallback } from "react";
import { Plug, Mail, CalendarDays, Zap, CheckCircle2, XCircle, History, Lock, Trash2, Send, Copy, ExternalLink } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATE_CHIP = {
  NOT_CONNECTED: { label: "Not connected", cls: "bg-secondary text-muted-foreground" },
  CONNECTING: { label: "Connecting…", cls: "bg-sky-500/10 text-sky-400" },
  CONNECTED: { label: "Connected", cls: "bg-emerald-500/10 text-emerald-400" },
  REAUTH_REQUIRED: { label: "Reconnect", cls: "bg-amber-500/10 text-amber-400" },
  PERMISSION_REQUIRED: { label: "Review permissions", cls: "bg-amber-500/10 text-amber-400" },
  ERROR: { label: "Connection issue", cls: "bg-red-500/10 text-red-400" },
  DISCONNECTED: { label: "Not connected", cls: "bg-secondary text-muted-foreground" },
  UNAVAILABLE: { label: "Unavailable", cls: "bg-secondary text-muted-foreground" },
};

const ICONS = { gmail_outlook: Mail, calendly: CalendarDays, zapier: Zap };

export default function IntegrationsPage() {
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    api.get("/integrations").then(({ data }) => setData(data)).catch(() => {});
    api.get("/integrations/logs").then(({ data }) => setLogs(data.logs || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const gmail = params.get("gmail");
    if (gmail === "connected") toast.success("Gmail connected — verified sends are live ✉️");
    if (gmail === "error") toast.error("Gmail connection failed — try again");
    if (gmail) { params.delete("gmail"); setParams(params, { replace: true }); }
  }, [params, setParams]);

  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="mx-auto max-w-3xl px-6 py-10 md:px-10">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Integrations</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">
        <Plug className="mr-1 inline h-6 w-6" /> Connect Zuntria to the tools that execute your goals
      </h1>
      <p className="mt-2 text-muted-foreground">
        Your connections power execution, automation and verified progress. Zuntria only marks important outcomes when the backend can verify what actually happened.
      </p>

      <div className="mt-8 space-y-4">
        {data.integrations.filter((i) => i.state !== "UNAVAILABLE").map((i) => (
          <IntegrationCard key={i.key} i={i} onChange={load} />
        ))}
        <ManualEmailCard />
      </div>

      <PlatformStack />
      <Roadmap items={data.integrations.filter((i) => i.state === "UNAVAILABLE")} />
      <AuditLog logs={logs} />
    </div>
  );
}

function ManualEmailCard() {
  const [text, setText] = useState("");
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadRecent = useCallback(() => {
    api.get("/executions/mine?kind=email_outreach").then(({ data }) => setRecent(data.executions || [])).catch(() => {});
  }, []);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  const copy = () => { navigator.clipboard.writeText(text); toast.success("Copied — paste it into your email app"); };
  const confirmSent = async () => {
    setBusy(true);
    try {
      await api.post("/executions/confirm", {
        kind: "email_outreach",
        title: text.trim().split("\n")[0].slice(0, 120) || "Outreach email",
      });
      toast.success("Recorded: you confirmed this was sent ✓");
      setText("");
      loadRecent();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not record confirmation"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="manual-email-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10"><Mail className="h-5 w-5 text-primary" /></span>
          <div>
            <h2 className="font-display text-lg">Email outreach <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">AI Email Drafts · you review & send</span></h2>
            <p className="text-xs text-muted-foreground">Write or paste your outreach, send it from your own email app, then confirm. Your confirmation is recorded — not independent delivery verification.</p>
          </div>
        </div>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Paste your pitch or follow-up here…"
        rows={4}
        className="mt-4 w-full rounded-xl border border-border bg-background p-3 text-sm outline-none focus:border-primary"
        data-testid="manual-email-text"
      />
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={copy} disabled={!text.trim()} data-testid="manual-email-copy"><Copy className="mr-1.5 h-3.5 w-3.5" />Copy</Button>
        <Button size="sm" variant="outline" asChild disabled={!text.trim()}>
          <a href={`mailto:?body=${encodeURIComponent(text.slice(0, 1500))}`} data-testid="manual-email-open">Open email app</a>
        </Button>
        <Button size="sm" onClick={confirmSent} disabled={busy || !text.trim()} data-testid="manual-email-confirm">{busy ? "Recording…" : "I sent this"}</Button>
      </div>
      {recent.length > 0 ? (
        <ul className="mt-3 space-y-1 border-t border-border pt-3">
          {recent.slice(0, 3).map((e) => (
            <li key={e.execution_id} className="flex items-center justify-between text-[11px] text-muted-foreground">
              <span><CheckCircle2 className="mr-1 inline h-3 w-3 text-emerald-400" />You confirmed this was sent — {e.title || "outreach"}</span>
              <span>{new Date(e.confirmed_at).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function IntegrationCard({ i, onChange }) {
  const Icon = ICONS[i.key] || Plug;
  const chip = STATE_CHIP[i.state] || STATE_CHIP.NOT_CONNECTED;
  const tierLabel = i.tier_required === "business" ? "Business" : "Pro";
  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid={`integration-${i.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10"><Icon className="h-5 w-5 text-primary" /></span>
          <div>
            <h2 className="font-display text-lg">{i.name}
              <span className="ml-2 rounded-full bg-accent/10 px-2 py-0.5 text-[11px] text-accent">{tierLabel}</span>
            </h2>
            <p className="text-xs text-muted-foreground">{i.description}</p>
            {i.account ? <p className="mt-0.5 text-xs text-emerald-400" data-testid={`integration-account-${i.key}`}>{i.account}</p> : null}
          </div>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs ${chip.cls}`} data-testid={`integration-state-${i.key}`}>{chip.label}</span>
      </div>
      {i.state === "UNAVAILABLE" ? (
        <p className="mt-4 rounded-xl border border-dashed border-border bg-background p-3 text-xs text-muted-foreground">
          Launching soon — this connection is being finalised. It will appear here the moment it's live.
        </p>
      ) : !i.entitled ? (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/5 p-3">
          <p className="text-xs text-muted-foreground"><Lock className="mr-1 inline h-3.5 w-3.5 text-amber-400" />Requires the {tierLabel} plan — enforced server-side.</p>
          <Button asChild size="sm" data-testid={`integration-upgrade-${i.key}`}><Link to="/pricing">Upgrade</Link></Button>
        </div>
      ) : i.key === "gmail_outlook" ? (
        <GmailActions i={i} onChange={onChange} />
      ) : i.key === "calendly" ? (
        <CalendlyActions i={i} onChange={onChange} />
      ) : i.key === "zapier" ? (
        <WebhooksManager onChange={onChange} />
      ) : null}
    </div>
  );
}

function GmailActions({ i, onChange }) {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/integrations/gmail/connect");
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start Gmail connection");
      setBusy(false);
    }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await api.post("/integrations/gmail/disconnect"); toast.success("Gmail disconnected"); onChange(); }
    catch { toast.error("Failed to disconnect"); }
    finally { setBusy(false); }
  };
  if (i.state === "CONNECTED") {
    return (
      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">Outreach sent through Gmail is verified by message ID — no false success.</p>
        <Button size="sm" variant="outline" onClick={disconnect} disabled={busy} data-testid="gmail-disconnect-btn">Disconnect</Button>
      </div>
    );
  }
  return <Button size="sm" className="mt-4" onClick={connect} disabled={busy} data-testid="integration-connect-gmail_outlook">{busy ? "Redirecting…" : "Connect Gmail"}</Button>;
}

function CalendlyActions({ i, onChange }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState(null);

  useEffect(() => {
    if (i.state === "CONNECTED") {
      api.get("/integrations/calendly/events").then(({ data }) => setEvents(data)).catch(() => setEvents({ events: [] }));
    }
  }, [i.state]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/calendly/connect", { token: token.trim() });
      toast.success(`Calendly connected${data.account ? ` (${data.account})` : ""} 📅`);
      setToken(""); setOpen(false); onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not verify token"); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await api.post("/integrations/calendly/disconnect"); toast.success("Calendly disconnected"); setEvents(null); onChange(); }
    catch { toast.error("Failed to disconnect"); }
    finally { setBusy(false); }
  };

  if (i.state === "CONNECTED") {
    return (
      <div className="mt-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          {events?.scheduling_url ? (
            <a href={events.scheduling_url} target="_blank" rel="noreferrer" className="text-xs text-primary underline" data-testid="calendly-scheduling-link">
              Your booking page <ExternalLink className="ml-0.5 inline h-3 w-3" />
            </a>
          ) : <span className="text-xs text-muted-foreground">Verified scheduling active</span>}
          <Button size="sm" variant="outline" onClick={disconnect} disabled={busy} data-testid="calendly-disconnect-btn">Disconnect</Button>
        </div>
        <div className="rounded-xl border border-border bg-background p-3" data-testid="calendly-upcoming-events">
          <p className="text-xs font-medium">Upcoming verified bookings</p>
          {!events ? <p className="mt-1 text-xs text-muted-foreground">Loading…</p>
            : events.events.length === 0 ? <p className="mt-1 text-xs text-muted-foreground">No upcoming meetings yet — share your booking page with a lead.</p>
            : (
              <ul className="mt-2 space-y-1.5">
                {events.events.slice(0, 5).map((e, idx) => (
                  <li key={idx} className="flex items-center justify-between text-xs">
                    <span className="font-medium">{e.name}</span>
                    <span className="text-muted-foreground">{new Date(e.start_time).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>
    );
  }
  return (
    <div className="mt-4 space-y-3">
      {!open ? (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setOpen(true)} data-testid="integration-connect-calendly">Connect Calendly</Button>
        </div>
      ) : (
        <div className="space-y-2 rounded-xl border border-border bg-background p-3">
          <p className="text-xs text-muted-foreground">
            Paste your Calendly <strong>Personal Access Token</strong>. Get one at Calendly → Integrations → API & Webhooks → Generate token.{" "}
            <a href="https://calendly.com/integrations/api_webhooks" target="_blank" rel="noreferrer" className="text-primary underline">Open Calendly</a>
          </p>
          <div className="flex gap-2">
            <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="eyJraWQiOi…" className="h-9" data-testid="calendly-token-input" autoComplete="off" />
            <Button size="sm" onClick={connect} disabled={busy || token.trim().length < 10} data-testid="calendly-connect-submit">{busy ? "Verifying…" : "Verify & connect"}</Button>
          </div>
        </div>
      )}
      <ManualBookingPath />
    </div>
  );
}

function ManualBookingPath() {
  const [link, setLink] = useState("");
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/executions/booking-link").then(({ data }) => { setSaved(data.url || ""); setLink(data.url || ""); }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/executions/booking-link", { url: link.trim() });
      setSaved(link.trim());
      toast.success(link.trim() ? "Booking link saved" : "Booking link cleared");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save link"); }
    finally { setBusy(false); }
  };
  const confirmBooked = async () => {
    try {
      await api.post("/executions/confirm", { kind: "booking_call", title: "Call booked via my booking page", meta: { link: saved } });
      toast.success("Recorded: you confirmed this call was booked ✓");
    } catch { toast.error("Could not record confirmation"); }
  };

  return (
    <div className="rounded-xl border border-dashed border-border bg-background p-3" data-testid="manual-booking-path">
      <p className="text-xs font-medium">Assisted booking — you send the link yourself</p>
      <p className="text-[11px] text-muted-foreground">Save your booking link, share it, then confirm when a call gets booked. Your confirmation is recorded — Zuntria does not independently verify the booking.</p>
      <div className="mt-2 flex gap-2">
        <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://calendly.com/your-name" className="h-8 text-xs" data-testid="booking-link-input" />
        <Button size="sm" variant="outline" className="h-8" onClick={save} disabled={busy} data-testid="booking-link-save">Save</Button>
      </div>
      {saved ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="h-8" asChild>
            <a href={saved} target="_blank" rel="noreferrer" data-testid="booking-link-open">Open booking link <ExternalLink className="ml-1 h-3 w-3" /></a>
          </Button>
          <Button size="sm" className="h-8" onClick={confirmBooked} data-testid="booking-confirm-btn">I booked the call</Button>
        </div>
      ) : null}
    </div>
  );
}

const EVENT_LABELS = {
  GOAL_CREATED: "Goal created", GOAL_COMPLETED: "Goal completed", TASK_COMPLETED: "Task completed",
  CLIENT_ACQUIRED: "Client acquired", PAYMENT_CONFIRMED: "Payment confirmed",
  OUTCOME_VERIFIED: "Outcome verified", APPLICATION_OFFER: "Job offer", CV_CREATED: "CV created",
};

function WebhooksManager({ onChange }) {
  const [hooks, setHooks] = useState(null);
  const [available, setAvailable] = useState([]);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [selected, setSelected] = useState(["PAYMENT_CONFIRMED", "CLIENT_ACQUIRED", "OUTCOME_VERIFIED"]);
  const [newSecret, setNewSecret] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [deliveriesFor, setDeliveriesFor] = useState(null);
  const [deliveries, setDeliveries] = useState([]);

  const load = useCallback(() => {
    api.get("/integrations/webhooks").then(({ data }) => { setHooks(data.webhooks || []); setAvailable(data.available_events || []); }).catch(() => setHooks([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/webhooks", { url: url.trim(), events: selected, label: label.trim() });
      setNewSecret({ id: data.webhook_id, secret: data.secret });
      setUrl(""); setLabel(""); setShowForm(false);
      toast.success("Webhook created — copy your signing secret now");
      load(); onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create webhook"); }
    finally { setBusy(false); }
  };
  const test = async (id) => {
    try {
      const { data } = await api.post(`/integrations/webhooks/${id}/test`);
      data.ok ? toast.success(`Test delivered (HTTP ${data.http_status}) ✅`) : toast.error(`Test failed: ${data.error || `HTTP ${data.http_status}`}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Test failed"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/integrations/webhooks/${id}`); toast.success("Webhook deleted"); if (newSecret?.id === id) setNewSecret(null); load(); onChange(); }
    catch { toast.error("Delete failed"); }
  };
  const enable = async (id) => {
    try { await api.post(`/integrations/webhooks/${id}/enable`); toast.success("Webhook re-enabled"); load(); }
    catch { toast.error("Failed"); }
  };
  const showDeliveries = async (id) => {
    if (deliveriesFor === id) { setDeliveriesFor(null); return; }
    const { data } = await api.get(`/integrations/webhooks/${id}/deliveries`).catch(() => ({ data: { deliveries: [] } }));
    setDeliveries(data.deliveries || []); setDeliveriesFor(id);
  };
  const toggleEvent = (e) => setSelected((s) => (s.includes(e) ? s.filter((x) => x !== e) : [...s, e]));

  if (!hooks) return <p className="mt-4 text-xs text-muted-foreground">Loading…</p>;

  return (
    <div className="mt-4 space-y-3" data-testid="webhooks-manager">
      <p className="text-xs text-muted-foreground">
        Use a <strong>Catch Hook</strong> URL from Zapier ("Webhooks by Zapier") or Make ("Custom webhook"). Every payload is HMAC-SHA256 signed (<code>X-Zuntria-Signature</code>).
      </p>
      {newSecret ? (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-3 text-xs" data-testid="webhook-new-secret">
          <p className="font-medium text-emerald-400">Signing secret (shown once):</p>
          <div className="mt-1 flex items-center gap-2">
            <code className="break-all">{newSecret.secret}</code>
            <button onClick={() => { navigator.clipboard.writeText(newSecret.secret); toast.success("Copied"); }} data-testid="webhook-copy-secret"><Copy className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      ) : null}
      {hooks.map((h) => (
        <div key={h.webhook_id} className="rounded-xl border border-border bg-background p-3" data-testid={`webhook-${h.webhook_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-xs font-medium">{h.label || h.url}</p>
              <p className="truncate text-[11px] text-muted-foreground">{h.url} · {h.events.map((e) => EVENT_LABELS[e] || e).join(", ")}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <span className={`rounded-full px-2 py-0.5 text-[11px] ${h.active ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>{h.active ? "Active" : "Paused"}</span>
              {!h.active ? <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => enable(h.webhook_id)} data-testid={`webhook-enable-${h.webhook_id}`}>Re-enable</Button> : null}
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => test(h.webhook_id)} data-testid={`webhook-test-${h.webhook_id}`}><Send className="mr-1 h-3 w-3" />Test</Button>
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => showDeliveries(h.webhook_id)} data-testid={`webhook-deliveries-${h.webhook_id}`}>Log</Button>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-red-400" onClick={() => remove(h.webhook_id)} data-testid={`webhook-delete-${h.webhook_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          {deliveriesFor === h.webhook_id ? (
            <ul className="mt-2 space-y-1 border-t border-border pt-2">
              {deliveries.length === 0 ? <li className="text-[11px] text-muted-foreground">No deliveries yet.</li> : deliveries.map((d) => (
                <li key={d.delivery_id} className="flex items-center justify-between text-[11px]">
                  <span>{d.status === "success" ? "✅" : "❌"} {EVENT_LABELS[d.event] || d.event} {d.http_status ? `(HTTP ${d.http_status})` : ""}</span>
                  <span className="text-muted-foreground">{new Date(d.created_at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ))}
      {showForm ? (
        <div className="space-y-2 rounded-xl border border-border bg-background p-3">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://hooks.zapier.com/hooks/catch/…" className="h-9" data-testid="webhook-url-input" />
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (optional)" className="h-9" data-testid="webhook-label-input" />
          <div className="flex flex-wrap gap-1.5">
            {available.map((e) => (
              <button key={e} onClick={() => toggleEvent(e)}
                className={`rounded-full border px-2.5 py-1 text-[11px] ${selected.includes(e) ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}
                data-testid={`webhook-event-${e}`}>
                {EVENT_LABELS[e] || e}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={create} disabled={busy || !url.trim() || selected.length === 0} data-testid="webhook-create-submit">{busy ? "Creating…" : "Create webhook"}</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <Button size="sm" onClick={() => setShowForm(true)} data-testid="webhook-add-btn">Add endpoint</Button>
      )}
    </div>
  );
}

const PLATFORM_STACK = [
  { name: "Stripe", desc: "Card payments — provider-verified settlement (signed webhooks)", live: true },
  { name: "PayPal", desc: "Payments & subscriptions — verified capture + signed webhooks", live: true },
  { name: "Revolut / Bank transfer", desc: "Manual payment links — admin-verified confirmation", live: true },
  { name: "Twilio WhatsApp", desc: "Reminders, briefings & alerts", live: true },
  { name: "OpenAI / Anthropic / Gemini", desc: "Goal intelligence & document generation", live: true },
  { name: "Job Boards (LinkedIn, Indeed, Reed, TotalJobs)", desc: "Tailored search links + tracked applications", live: true },
];

function PlatformStack() {
  return (
    <div className="mt-10" data-testid="platform-stack">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Working for you today</p>
      <div className="mt-4 rounded-2xl border border-border bg-card p-5">
        <ul className="space-y-2.5">
          {PLATFORM_STACK.map((i) => (
            <li key={i.name} className="flex items-center justify-between gap-2 text-sm">
              <span>
                <span className="font-medium">{i.name}</span>
                <span className="block text-xs text-muted-foreground">{i.desc}</span>
              </span>
              <span className="shrink-0 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-400">Live</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const ROADMAP_STATIC = ["Outlook", "Slack", "Discord", "Zoom", "Google Meet", "Plaid", "Wise", "DeepL"];

function Roadmap({ items }) {
  const names = [...items.map((i) => i.name), ...ROADMAP_STATIC];
  return (
    <details className="mt-6 rounded-2xl border border-border bg-card/50 p-5" data-testid="integrations-roadmap">
      <summary className="cursor-pointer text-sm text-muted-foreground">On the roadmap ({names.length}) — not yet active, shown here for transparency</summary>
      <div className="mt-3 flex flex-wrap gap-2">
        {names.map((n) => (
          <span key={n} className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground">{n}</span>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">Zuntria never shows an integration as connected unless the backend has verified it. These will appear above the moment they're genuinely live.</p>
    </details>
  );
}

function AuditLog({ logs }) {
  if (!logs.length) return null;
  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-6 md:p-8" data-testid="integration-logs">
      <div className="flex items-center gap-2">
        <History className="h-4 w-4 text-primary" />
        <h2 className="font-display text-lg">Activity</h2>
      </div>
      <ul className="mt-3 space-y-1.5">
        {logs.map((l) => (
          <li key={l.log_id} className="flex items-start gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs">
            {l.status === "success"
              ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
              : <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" />}
            <span className="flex-1">
              <span className="font-medium capitalize">{l.provider} · {l.action.replace("_", " ")}</span>
              {l.detail ? <span className="text-muted-foreground"> — {l.detail}</span> : null}
            </span>
            <span className="shrink-0 text-muted-foreground">{new Date(l.created_at).toLocaleString()}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
